// src/pages/UserList.js
import React, { useEffect, useMemo, useState } from "react";
import {
  Table,
  Button,
  Modal,
  Form,
  Alert,
  Spinner,
  Pagination,
  Row,
  Col,
  Card,
} from "react-bootstrap";
import api from "../../api.js";
import Cookies from "js-cookie";
import moment from "jalali-moment";
import Chart from "react-apexcharts";

/**
 * UserList with Monthly Overview (pie / bar / line / heatmap)
 * - robust (handles missing data)
 * - mobile friendly (bootstrap grid + responsive charts)
 * - tooltips show hours:minutes
 *
 * Note: make sure `apexcharts` and `react-apexcharts` are installed in your project:
 * npm install apexcharts react-apexcharts
 */

export default function UserList() {
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [fields, setFields] = useState([]);
  const [loading, setLoading] = useState(true);
  const [msg, setMsg] = useState("");
  const [selectedUser, setSelectedUser] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [downloadExcelModal, setdownloadExcelModal] = useState(false);
  const [showOverviewModal, setShowOverviewModal] = useState(false);
  const [monthlyOverview, setMonthlyOverview] = useState(null);
  const [search, setSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 10;

  // Jalali month/year picker
  const today = moment();
  const [selectedMonth, setSelectedMonth] = useState(today.jMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(today.jYear());

  const [selectedMonthexcel, setSelectedMonthexcel] = useState(today.jMonth() + 1);
  const [selectedYearexcel, setSelectedYearexcel] = useState(today.jYear());

  const presenceLabels = {
  "on-site": "حضوری",
  "remote": "دورکار",
  "": "-", // fallback
};
const toPersianNumber = (num) => {
  const persianDigits = ["۰","۱","۲","۳","۴","۵","۶","۷","۸","۹"];
  return num.toString().replace(/\d/g, (d) => persianDigits[d]);
};


  const token = Cookies.get("access_token");
  const username = Cookies.get("username");

  useEffect(() => {
    fetchUsers();
    fetchFields();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchUsers = async () => {
    try {
      const res = await api.get("/users/", {
        headers: { Authorization: `Bearer ${token}` },
      });
      setUsers(res.data || []);
      setFilteredUsers(res.data || []);
      setLoading(false);
    } catch (err) {
      console.error(err);
      setMsg("❌ خطا در دریافت لیست کاربران");
      setLoading(false);
    }
  };

  const fetchFields = async () => {
    try {
      const res = await api.get("/field/myfields/", {
        headers: { Authorization: `Bearer ${token}` },
      });
      setFields(res.data || []);
    } catch (err) {
      console.error(err);
    }
  };

  // Search
  const handleSearch = (e) => {
    const text = e.target.value || "";
    setSearch(text);
    const filtered = users.filter(
      (u) =>
        (u.username || "")
          .toString()
          .toLowerCase()
          .includes(text.toLowerCase()) ||
        (u.email || "").toString().toLowerCase().includes(text.toLowerCase()) ||
        (u.role || "").toString().toLowerCase().includes(text.toLowerCase())
    );
    setFilteredUsers(filtered);
    setCurrentPage(1);
  };

  // Pagination calculations
  const indexOfLast = currentPage * pageSize;
  const indexOfFirst = indexOfLast - pageSize;
  const currentUsers = filteredUsers.slice(indexOfFirst, indexOfLast);
  const totalPages = Math.max(1, Math.ceil(filteredUsers.length / pageSize));
  const handlePageChange = (page) => setCurrentPage(page);

  // Edit user
  const handleEditClick = (user) => {
    setSelectedUser({
      id: user.id,
      role: user.role,
      accessible_fields: (user.accessible_fields || []).map((f) =>
        typeof f === "object" && f.id ? f.id : f
      ),
    });
    setShowEditModal(true);
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    if (!selectedUser) return;
    try {
      await api.patch(`/users/${selectedUser.id}/`, selectedUser, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setMsg("✅ تغییرات با موفقیت ذخیره شد!");
      setShowEditModal(false);
      fetchUsers();
    } catch (err) {
      console.error(err);
      setMsg("❌ خطا در ذخیره تغییرات");
    }
  };

  const handleOverviewExcelClick = () => {
    // default month/year to today if none set
    setSelectedMonthexcel(today.jMonth() + 1);
    setSelectedYearexcel(today.jYear());
    setdownloadExcelModal(true);
  };

  const downloadExcel = async () => {
  try{
    const jYear = selectedYearexcel;
    const jMonth = selectedMonthexcel;

    const res = await api.get(`/monthly-overview-excel/${jYear}/${jMonth}/`,
      {
        headers: {Authorization: `Bearer ${token}`},
        responseType: "blob"
      }
    );
    const blob = new Blob([res.data], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `monthly_overview_${jYear}_${jMonth}`;
    a.click();
    window.URL.revokeObjectURL(url);
  } catch(err){
    alert("خطا در دانلود فایل");
    console.error(err);
  }
  };

  // Monthly overview
  const handleOverviewClick = (user) => {
    setSelectedUser(user);
    setMonthlyOverview(null);
    // default month/year to today if none set
    setSelectedMonth(today.jMonth() + 1);
    setSelectedYear(today.jYear());
    setShowOverviewModal(true);
  };

  const fetchOverview = async () => {
    if (!selectedUser) return;
    try {
      // ensure numbers
      const jYear = Number(selectedYear);
      const jMonth = Number(selectedMonth);
      const res = await api.get(
        `/users/${selectedUser.id}/overview/${jYear}/${jMonth}/`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setMonthlyOverview(res.data || null);
    } catch (err) {
      console.error(err);
      setMsg("❌ خطا در دریافت گزارش ماهانه");
      setMonthlyOverview(null);
    }
  };

  // Precompute chart data with useMemo for performance
  const { pieOptions, pieSeries, barOptions, barSeries } =
    useMemo(() => {
      if (!monthlyOverview) {
        return {
          pieOptions: {},
          pieSeries: [],
          barOptions: {},
          barSeries: [],
        };
      }

      // safe getters
      const safe = (k) => Number(monthlyOverview[k] || 0);
      const otherTitles = monthlyOverview.total_other_titles || {};

      // PIE: minutes per activity (main titles + other)
      const pieLabels = [
        "گزارش فناوری",
        "امتیازدهی فناوری",
        "گزارش تجمیعی",
        "بارگزاری در سایت",
        ...Object.keys(otherTitles),
      ];
      const pieSeriesArr = [
        safe("total_report_minutes"),
        safe("total_scoring_minutes"),
        safe("total_aggregate_minutes"),
        safe("total_upload_minutes"),
        ...Object.values(otherTitles).map((v) => Number(v || 0)),
      ];

      // BAR: counts for main three titles
      const barLabels = ["گزارش فناوری", "امتیازدهی فناوری", "بارگزاری در سایت"];
      const barSeriesArr = [
        {
          name: "تعداد گزارش‌ها",
          data: [
            Number(monthlyOverview.total_report_count || 0),
            Number(monthlyOverview.total_scoring_report_count || 0),
            Number(monthlyOverview.total_user_reports || 0), // using total_user_reports as upload count fallback
          ],
        },
      ];


      // COMMON tooltip formatter => convert minutes to H:M string
      const tooltipYFormatter = (val) => {
        const v = Number(val || 0);
        const h = Math.floor(v / 60);
        const m = Math.floor(v % 60);
        return `${h} س${m}د`;
      };

      // Persian number helper
const toPersianNumber = (num) => {
  const persianDigits = ["۰","۱","۲","۳","۴","۵","۶","۷","۸","۹"];
  return num.toString().replace(/\d/g, (d) => persianDigits[d]);
};

// PIE chart
const pieOpts = {
  chart: { type: "pie", foreColor: "#000", toolbar: { show: false }, parentHeightOffset: 0 },
  labels: pieLabels,
  legend: { position: "bottom" },
  tooltip: {
    enabled: true,
    y: {
      formatter: (val) => {
        const h = Math.floor(val / 60);
        const m = Math.floor(val % 60);
        return `${toPersianNumber(h)}س ${toPersianNumber(m)}د`;
      },
    },
    style: { fontFamily: "IranSans" },
  },
  dataLabels: {
    enabled: true,
    formatter: (val, opts) => {
      // val is percentage (0-100)
      return toPersianNumber(val.toFixed(1)) + "%";
    },
    style: { fontSize: "12px", fontFamily: "IranSans" },
  },
  responsive: [
    {
      breakpoint: 768,
      options: { chart: { width: "100%" }, legend: { position: "bottom" } },
    },
  ],
};





      const barOpts = {
  chart: { toolbar: { show: true } },
  plotOptions: { bar: { horizontal: false, columnWidth: "45%" } },
  dataLabels: { enabled: false },
  xaxis: { categories: barLabels },
  yaxis: {
    title: { text: "تعداد" },
    labels: {
      formatter: (val) => toPersianNumber(val),
    },
  },
  tooltip: {
    y: {
      formatter: (val) => toPersianNumber(val),
    },
  },
};





      return {
        pieOptions: pieOpts,
        pieSeries: pieSeriesArr,
        barOptions: barOpts,
        barSeries: barSeriesArr,
      };
    }, [monthlyOverview]);

  if (loading) return <p>در حال بارگذاری...</p>;

  const jalaliYears = Array.from({ length: 6 }, (_, i) => today.jYear() - i);

  // small helper to show HM from monthlyOverview props (if stored as minutes)
  const toHM = (maybe) => {
    const val = Number(maybe || 0);
    const h = Math.floor(val / 60);
    const m = Math.floor(val % 60);
    return `${h}س ${m}د`;
  };

  return (
    <div className="container mt-3">
      <h3 className="title mb-3 text-center">لیست کاربران</h3>
      {msg && <Alert variant="info">{msg}</Alert>}
      
      <Form.Control
        type="text"
        placeholder="جستجو بر اساس نام کاربری، ایمیل یا نقش..."
        className="mb-3"
        value={search}
        onChange={handleSearch}
      />

      <Table striped bordered hover responsive>
        <thead>
          <tr>
            <th>نام کاربری</th>
            <th>ایمیل</th>
            <th>عملیات</th>
          </tr>
        </thead>
        <tbody>
          {currentUsers.map((u) => (
            <tr key={u.id}>
              <td style={{ minWidth: 140 }}>{u.username}</td>
              <td style={{ minWidth: 220 }}>{u.email}</td>
              <td style={{ minWidth: 200 }}>
                <Button
                  size="sm"
                  className="btn-edit me-2"
                  onClick={() => handleEditClick(u)}
                >
                  ویرایش
                </Button>
                {(username === "a_zabihollahpoor" || username === "f_rahiminezhad" || username === "rahino_admin") && (
                  <Button
                  size="sm"
                  className="btn-submit"
                  variant="secondary"
                  onClick={() => handleOverviewClick(u)}
                >
                  گزارش ماهانه
                </Button>
              )}
              </td>
            </tr>
          ))}
        </tbody>
      </Table>

      <Pagination className="justify-content-center mt-3">
        {[...Array(totalPages)].map((_, idx) => (
          <Pagination.Item
            key={idx + 1}
            active={idx + 1 === currentPage}
            onClick={() => handlePageChange(idx + 1)}
          >
            {idx + 1}
          </Pagination.Item>
        ))}
      </Pagination>
      {(username === "a_zabihollahpoor" || username === "f_rahiminezhad" || username === "rahino_admin") && (
      <Button className="btn-submit btn-sm fixed-download-btn mb-3" onClick={() => handleOverviewExcelClick()}>
        دانلود گزارش ماهانه
      </Button>
      )}

      {/* Edit User Modal */}
      <Modal show={showEditModal} onHide={() => setShowEditModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>ویرایش دسترسی‌ها</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedUser ? (
            <Form onSubmit={handleEditSubmit}>
              <Form.Group className="mb-2">
                <Form.Label>نقش</Form.Label>
                <Form.Select
                  value={selectedUser.role}
                  onChange={(e) =>
                    setSelectedUser({ ...selectedUser, role: e.target.value })
                  }
                >
                  <option value="manager">مدیر</option>
                  <option value="author">نویسنده</option>
                  <option value="contributer">همکار</option>
                  <option value="subscriber">مشترک</option>
                </Form.Select>
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>دسترسی به فیلدها</Form.Label>
                <div style={{ maxHeight: 220, overflowY: "auto", padding: 6 }}>
                  {fields.map((field) => (
                    <Form.Check
                      key={field.id}
                      type="checkbox"
                      label={field.title}
                      checked={selectedUser.accessible_fields?.includes(field.id)}
                      onChange={(e) => {
                        let updated = [...(selectedUser.accessible_fields || [])];
                        if (e.target.checked) updated.push(field.id);
                        else updated = updated.filter((id) => id !== field.id);
                        setSelectedUser({ ...selectedUser, accessible_fields: updated });
                      }}
                    />
                  ))}
                </div>
              </Form.Group>

              <Button type="submit" className="btn-submit mt-2 w-100">
                ذخیره تغییرات
              </Button>
            </Form>
          ) : (
            <Spinner />
          )}
        </Modal.Body>
      </Modal>
      <Modal
        show={downloadExcelModal}
        onHide={() => setdownloadExcelModal(false)}
        size="xl"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>
           دانلود گزارش ماهانه
          </Modal.Title>
        </Modal.Header>

        <Modal.Body>
          {/* Month + Year selector */}
          
            <Form className="mb-3 d-flex flex-wrap gap-2 align-items-end">
              <Form.Group style={{ minWidth: 120 }}>
                <Form.Label>ماه</Form.Label>
                <Form.Select
                  value={selectedMonthexcel}
                  onChange={(e) => setSelectedMonthexcel(Number(e.target.value))}
                >
                  {[...Array(12)].map((_, idx) => (
                    <option key={idx + 1} value={idx + 1}>
                      {toPersianNumber(idx + 1)}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>

              <Form.Group style={{ minWidth: 140 }}>
                <Form.Label>سال </Form.Label>
                <Form.Select
                  value={selectedYearexcel}
                  onChange={(e) => setSelectedYearexcel(Number(e.target.value))}
                >
                  {jalaliYears.map((y) => (
                    <option key={y} value={y}>
                      {toPersianNumber(y)}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>

              <Button variant="primary" onClick={downloadExcel}>
                دانلود
              </Button>
            </Form>
          
          </Modal.Body>
          </Modal>

      {/* Monthly Overview Modal */}
      <Modal
        show={showOverviewModal}
        onHide={() => setShowOverviewModal(false)}
        size="xl"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>
            گزارش ماهانه {selectedUser?.username}
            {monthlyOverview ? ` — ${toPersianNumber(monthlyOverview.j_year)}/${toPersianNumber(monthlyOverview.j_month)}` : ""}
          </Modal.Title>
        </Modal.Header>

        <Modal.Body>
          {/* Month + Year selector */}
          {selectedUser && (
            <Form className="mb-3 d-flex flex-wrap gap-2 align-items-end">
              <Form.Group style={{ minWidth: 120 }}>
                <Form.Label>ماه</Form.Label>
                <Form.Select
                  value={selectedMonth}
                  onChange={(e) => setSelectedMonth(Number(e.target.value))}
                >
                  {[...Array(12)].map((_, idx) => (
                    <option key={idx + 1} value={idx + 1}>
                      {toPersianNumber(idx + 1)}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>

              <Form.Group style={{ minWidth: 140 }}>
                <Form.Label>سال </Form.Label>
                <Form.Select
                  value={selectedYear}
                  onChange={(e) => setSelectedYear(Number(e.target.value))}
                >
                  {jalaliYears.map((y) => (
                    <option key={y} value={y}>
                      {toPersianNumber(y)}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>

              <Button variant="primary" onClick={fetchOverview}>
                مشاهده
              </Button>
            </Form>
          )}

          {/* Overview content */}
          {monthlyOverview ? (
            <>
              <Row className="g-3 mb-3">
                <Col md={4} sm={12}>
                  <Card className="p-3 h-100">
                    <h6 className="mb-2">فعالیت ها</h6>
                    <div>گزارش فناوری: {toPersianNumber(toHM(monthlyOverview.total_report_minutes))}</div>
                      <div>
                        امتیازدهی فناوری: {toPersianNumber(toHM(monthlyOverview.total_scoring_minutes))}
                      </div>
                      <div>بارگذاری در سایت: {toPersianNumber(toHM(monthlyOverview.total_upload_minutes))}</div>
                      <div>گزارش تجمیعی: {toPersianNumber(toHM(monthlyOverview.total_aggregate_minutes))}</div>

                    {/* Other titles listed here */}
                    <div className="mt-2">
                      {monthlyOverview.total_other_titles &&
                        Object.entries(monthlyOverview.total_other_titles).map(
                          ([title, mins]) => (
                            <div key={title} className="small text-secondary">
                              • {title}: {toPersianNumber(toHM(mins))}
                            </div>
                          )
                        )}
                    </div>
                  </Card>
                </Col>

                <Col md={4} sm={12}>
                  <Card className="p-3 h-100">
                    <h6 className="mb-2">ساعات کاری</h6>
                    <div style={{ fontSize: 18, fontWeight: 700 }}>
                      {toPersianNumber(monthlyOverview.total_work_hm?.[0]) ?? 0} ساعت و{" "}
                      {toPersianNumber(monthlyOverview.total_work_hm?.[1]) ?? 0} دقیقه
                    </div>
                    <div className="text-muted small mt-1">
                      مجموع دقیقه: {toPersianNumber(monthlyOverview.total_work_minutes) ?? 0}
                    </div>
                    <div className="text-muted small mt-1">
  متوسط زمان گزارش فناوری: {toPersianNumber(monthlyOverview.report_ratio_hm?.[0])}س {toPersianNumber(monthlyOverview.report_ratio_hm?.[1])}د
</div>
<div className="text-muted small mt-1">
  متوسط زمان امتیازدهی فناوری: {toPersianNumber(monthlyOverview.scoring_ratio_hm?.[0])}س {toPersianNumber(monthlyOverview.scoring_ratio_hm?.[1])}د
</div>
<div className="text-muted small mt-1">
  متوسط زمان بارگزاری در سایت: {toPersianNumber(monthlyOverview.upload_ratio_hm?.[0])}س {toPersianNumber(monthlyOverview.upload_ratio_hm?.[1])}د
</div>
<div className="text-muted small mt-1"> * زمان به ازای هر گزارش</div>

                    
                  </Card>
                </Col>

                <Col md={4} sm={12}>
                  <Card className="p-3 h-100">
                    <h6 className="mb-2">حضور</h6>
                    <div>روزهای معادل حضوری: {toPersianNumber(Math.round(monthlyOverview.days_on_site * 10) / 10)}</div>
                    

                  <div className="text-muted small mt-1">نوع حضور غالب: {presenceLabels[monthlyOverview.most_presence_type] || "-"}</div>

                    <div className="mt-2 small text-secondary">
                      شمار گزارش های بارگزاری شده: {toPersianNumber(monthlyOverview.total_user_reports) ?? 0}
                    </div>
                    <div className="mt-2 small text-secondary">
                      شمار گزارش های امتیازدهی شده: {toPersianNumber(monthlyOverview.total_scoring_report_count) ?? 0}
                    </div>
                    <div className="mt-2 small text-secondary">
                      شمار گزارش های فناوری: {toPersianNumber(monthlyOverview.total_report_count) ?? 0}
                    </div>
                  </Card>
                </Col>
              </Row>

              {/* Charts row */}
              <Row className="g-3">
                <Col lg={6} md={12} sm={12}>
                  <Card className="p-3 h-100">
                    <h6 className="mb-2">تقسیم‌بندی ساعات فعالیت‌ها</h6>
                    <Chart
                      type="pie"
                      height={320}
                      series={pieSeries}
                      options={pieOptions}
                    />
                    <div className="text-muted small mt-2">
                      مقدارها بر حسب دقیقه نشان داده شده‌اند. نشانگر را نگه دارید تا زمان به صورت ساعت:دقیقه نمایش یابد.
                    </div>
                  </Card>
                </Col>

                
              </Row>

              {/* Line + Heatmap */}
              <Row className="g-3 mt-3">
                

                <Col lg={6} md={12} sm={12}>
                  <Card className="p-3 h-100">
                    <h6 className="mb-2">تعداد گزارش‌ها (مقایسه)</h6>
                    <Chart
                      type="bar"
                      height={320}
                      series={barSeries}
                      options={barOptions}
                    />
                    <div className="text-muted small mt-2">ستون‌ها تعداد گزارش‌ها را نشان می‌دهند.</div>
                  </Card>
                </Col>
              </Row>
            </>
          ) : (
            <div className="text-center py-4">
              <div>برای این ماه گزارشی یافت نشد.</div>
              <div className="text-muted small mt-2">
                روی "مشاهده" کلیک کنید تا محاسبه و بارگذاری شود.
              </div>
            </div>
          )}
        </Modal.Body>
      </Modal>
    </div>
  );
}
